import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sprout, Leaf, Sun, MapPin } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";

const heroBg = "https://images.unsplash.com/photo-1713815715073-e7c67d35cdfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxjYWxpZm9ybmlhJTIwcm9sbGluZyUyMGhpbGxzJTIwc3Vuc2V0fGVufDB8fHx8MTc4MTQ2Mjc2MXww&ixlib=rb-4.1.0&q=85";
const storyImg = "https://images.unsplash.com/photo-1693414854278-6b3703411629?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxoYW5kcyUyMHBsYW50aW5nJTIwc2VlZGxpbmclMjBpbiUyMHNvaWx8ZW58MHx8fHwxNzgxNDYyNzYxfDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [proof, setProof] = useState(null);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.slice(0, 8))).catch(() => {});
    api.get("/products/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/stats/social-proof").then(({ data }) => setProof(data)).catch(() => {});
  }, []);

  const proofStats = proof
    ? [
        proof.rating_avg ? { val: `${proof.rating_avg.toFixed(1)}★`, label: `${proof.review_count} grower review${proof.review_count === 1 ? "" : "s"}` } : null,
        proof.variety_count ? { val: `${proof.variety_count}`, label: "Seed varieties" } : null,
        proof.packets_shipped ? { val: `${proof.packets_shipped.toLocaleString()}+`, label: "Packets shipped" } : null,
        { val: "$3.50", label: "Every packet, flat" },
      ].filter(Boolean)
    : [];

  return (
    <div data-testid="home-page">
      <Seo
        title="Natural Seed Shop in Amador County, CA"
        description="Naturally sourced, non-GMO garden seeds hand-packed in Amador County, CA. 30+ vegetable, herb & flower varieties at $3.50 a packet. Free local pickup in Plymouth, CA."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Rooted in Amador",
          description: "Boutique seed shop selling naturally sourced, non-GMO garden seeds at $3.50 a packet.",
          areaServed: "Amador County, California",
          address: { "@type": "PostalAddress", addressLocality: "Plymouth", addressRegion: "CA", addressCountry: "US" },
          url: typeof window !== "undefined" ? window.location.origin : "",
        }}
      />
      {/* HERO - asymmetric split */}
      <section className="relative">
        <div className="grid lg:grid-cols-12 min-h-[90vh]">
          <div className="lg:col-span-5 flex items-center px-6 lg:px-16 py-20 bg-cream">
            <div className="animate-fade-up">
              <div className="text-xs tracking-wider-2 uppercase text-sage mb-6 flex items-center gap-2" data-testid="hero-overline">
                <span className="w-8 h-px bg-sage" /> Amador County, California
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-darkRoast">
                Grow something <em className="not-italic text-sage">real.</em>
              </h1>
              <p className="mt-8 text-base sm:text-lg text-earth max-w-md leading-relaxed">
                Naturally sourced, non-GMO seeds, packed by hand in California's gold country.
                Every packet, <strong className="text-darkRoast">$3.50</strong>. Honest pricing on naturally sourced seed, with none of the markup.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/shop"
                  className="inline-flex items-center gap-2 bg-darkRoast text-cream rounded-full px-7 py-3.5 text-sm tracking-wider-2 uppercase hover:bg-sage transition-colors"
                  data-testid="hero-shop-btn"
                >
                  Shop seeds <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 border border-darkRoast/20 text-darkRoast rounded-full px-7 py-3.5 text-sm tracking-wider-2 uppercase hover:border-sage hover:text-sage transition-colors"
                  data-testid="hero-about-btn"
                >
                  Our story
                </Link>
              </div>

              <div className="mt-16 grid grid-cols-3 gap-6 max-w-md">
                {[
                  { icon: Leaf, label: "Non-GMO" },
                  { icon: Sun, label: "Naturally Sourced" },
                  { icon: MapPin, label: "Locally Packed" },
                ].map((b) => (
                  <div key={b.label} className="text-xs tracking-wider-2 uppercase text-earth">
                    <b.icon className="w-5 h-5 mb-2 text-sage" strokeWidth={1.5} />
                    {b.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 relative overflow-hidden bg-surface grain-overlay min-h-[400px]">
            <img src={heroBg} alt="Amador rolling hills" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute bottom-8 left-8 right-8 lg:bottom-12 lg:left-12 lg:right-12">
              <div className="inline-block bg-cream/90 backdrop-blur-sm px-6 py-4 rounded-sm">
                <div className="text-[10px] tracking-wider-2 uppercase text-earth">Amador County, CA</div>
                <div className="font-display text-2xl text-darkRoast">Small batch &middot; Non-GMO &middot; Hand packed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof stats strip */}
      {proofStats.length > 0 && (
        <section className="border-y border-darkRoast/10 bg-surface" data-testid="social-proof-strip">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {proofStats.map((s) => (
              <div key={s.label} className="text-center md:text-left" data-testid={`proof-stat-${s.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className="font-display text-3xl sm:text-4xl text-darkRoast">{s.val}</div>
                <div className="text-xs tracking-wider-2 uppercase text-earth mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">Browse the catalog</div>
            <h2 className="font-display text-4xl sm:text-5xl text-darkRoast">By Category</h2>
          </div>
          <Link to="/shop" className="text-sm tracking-wider-2 uppercase text-darkRoast hover:text-sage flex items-center gap-2" data-testid="view-all-link">
            View all seeds <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((c, i) => (
            <Link
              key={c}
              to={`/shop?category=${encodeURIComponent(c)}`}
              className="group relative aspect-[4/5] overflow-hidden rounded-sm border border-darkRoast/10 bg-surface"
              data-testid={`category-tile-${c.toLowerCase()}`}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-darkRoast/70 via-darkRoast/10 to-transparent z-10" />
              <div className="absolute inset-0 flex flex-col justify-end p-6 z-20">
                <div className="text-[10px] tracking-wider-2 uppercase text-cream/80 mb-1">Seeds</div>
                <div className="font-display text-2xl text-cream">{c}</div>
              </div>
              <Sprout className="absolute top-6 right-6 w-6 h-6 text-cream/70 z-20" strokeWidth={1.2} />
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="bg-surface py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">Just packed</div>
            <h2 className="font-display text-4xl sm:text-5xl text-darkRoast">Fresh from the bench</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => (
              <Link
                key={p.product_id}
                to={`/product/${p.product_id}`}
                className="group flex flex-col h-full"
                data-testid={`featured-product-${p.product_id}`}
              >
                <div className="aspect-square overflow-hidden bg-cream border border-darkRoast/10 rounded-sm">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="mt-4 flex flex-col flex-1">
                  <div className="text-[10px] tracking-wider-2 uppercase text-sage mb-1">{p.category}</div>
                  <h3 className="font-display text-xl text-darkRoast leading-tight">{p.name}</h3>
                  <div className="flex items-center justify-between mt-auto pt-2 text-sm">
                    <span className="text-earth">{p.packet_size_g}g packet</span>
                    <span className="font-medium text-darkRoast">${p.price.toFixed(2)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Affordability banner */}
      <section className="bg-darkRoast text-cream py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs tracking-wider-2 uppercase text-sage mb-4">Why $3.50</div>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">Organic seeds shouldn't cost $5 a packet.</h2>
          </div>
          <p className="text-cream/80 leading-relaxed">
            Most organic seed packets this size cost <em className="not-italic font-medium">five dollars</em> or
            more. We think growing real food at home shouldn't be a luxury, so we keep every packet at
            $3.50. Same naturally sourced, non-GMO seed, without the markup.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="aspect-[4/5] overflow-hidden rounded-sm border border-darkRoast/10">
            <img src={storyImg} alt="Hands planting a seedling" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-xs tracking-wider-2 uppercase text-sage mb-4">Our story</div>
            <h2 className="font-display text-4xl sm:text-5xl text-darkRoast leading-tight">
              From foothill soil to your garden bed.
            </h2>
            <p className="mt-6 text-earth leading-relaxed">
              Rooted in Amador was started by a young family putting down roots in California's
              gold country. Our family has called these foothills home for over 30 years, and
              we came back to raise the next generation in the same place we grew up.
            </p>
            <p className="mt-4 text-earth leading-relaxed">
              We started this shop because organic seed packets typically cost $5 or more. We
              think growing real, non-GMO food at home shouldn't be a luxury, so we sell
              every packet for $3.50.
            </p>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 text-darkRoast hover:text-sage text-sm tracking-wider-2 uppercase"
              data-testid="story-readmore"
            >
              Read more <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* Request a seed CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
        <div className="border border-darkRoast/15 bg-surface p-10 md:p-14 rounded-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">Limited selection, for now</div>
            <h3 className="font-display text-3xl text-darkRoast leading-tight max-w-xl">
              Looking for a variety we don't carry yet?
            </h3>
            <p className="mt-3 text-earth max-w-lg">
              Send us a request. We can't promise anything, but we'll try to make it happen.
            </p>
          </div>
          <Link to="/about#seed-request" className="bg-darkRoast text-cream rounded-full px-7 py-3.5 text-sm tracking-wider-2 uppercase hover:bg-sage transition-colors whitespace-nowrap" data-testid="home-request-seed-btn">
            Request a seed
          </Link>
        </div>
      </section>
    </div>
  );
}
